import React from "react"
import { useSnapshot } from "valtio"
import { Box } from "@mui/material"
import { HotTextScoringContext } from "./HotTextScoringContext"
import { DomNodes } from "../../constants"
import {
  getChildrenWithInjectedProps,
  transformChoicesConstraints,
  useAssessmentItemResponseScoring,
  useSelectableChoice
} from "../../helpers"
import { ConstraintsAlertContainer } from "../../ui/containers/ConstraintsAlertContainer"
import { CurrentTestState } from "../../storage"
import {
  CheckCorrect,
  InteractionType,
  onSaveStudentAnswer,
  ContainerType,
  NeutralSetting
} from "../../types"

interface HotTextInteractionProps {
  itemId: string
  responseidentifier: string
  minchoices: string
  maxchoices: string
  children?: JSX.Element[]
  accessibilityAttr?: { [key: string]: string }
  onSaveStudentAnswer?: onSaveStudentAnswer
  checkCorrect?: CheckCorrect
  containerType: ContainerType
  isNeutralSetting?: NeutralSetting
}

export const HotTextInteractionComponent: React.FC<
  HotTextInteractionProps
> = props => {
  const { currentItemResponse } = useSnapshot(CurrentTestState)
  const {
    responseidentifier: responseDeclarationId,
    maxchoices,
    minchoices,
    children,
    accessibilityAttr,
    onSaveStudentAnswer,
    containerType,
    isNeutralSetting
  } = props

  const [minChoices, maxChoices] = transformChoicesConstraints(
    minchoices,
    maxchoices
  )

  const {
    selected: selectedChoices,
    onSelect,
    responseDeclaration,
    isValid
  } = useSelectableChoice({
    responseIdentifier: responseDeclarationId,
    interactionType: InteractionType.hotText,
    constraints: {
      minChoices,
      maxChoices
    },
    onSaveStudentAnswer
  })

  const { groupMode, correctResponse } = useAssessmentItemResponseScoring(
    currentItemResponse?.itemId,
    responseDeclarationId
  )

  const childrenWithProps = getChildrenWithInjectedProps(
    DomNodes.hotText,
    children,
    {
      minChoices,
      maxChoices,
      responseDeclaration,
      onSelect: (id: string) => onSelect(id),
      selectedChoices: selectedChoices || [],
      isValid
    }
  )

  return (
    <>
      {containerType === "assessment" && (
        <ConstraintsAlertContainer
          minChoices={minChoices}
          maxChoices={maxChoices}
          selectedCount={selectedChoices?.length}
        />
      )}
      {currentItemResponse && responseDeclaration && (
        <Box {...accessibilityAttr}>
          <HotTextScoringContext.Provider
            value={{ correctResponses: correctResponse, groupMode }}
          >
            {childrenWithProps}
          </HotTextScoringContext.Provider>
        </Box>
      )}
    </>
  )
}
